import { supabase } from '../lib/supabase';

/**
 * Service for tracking prompt generations and analytics
 * Handles snake_case (database) to camelCase (React) conversion
 */
export const promptGenerationService = {
  /**
   * Track a prompt generation
   * @param {Object} generationData - Prompt generation data
   * @returns {Promise<Object>} Created generation record
   */
  async trackGeneration(generationData) {
    try {
      const { data, error } = await supabase?.from('prompt_generations')?.insert({
          use_case_id: generationData?.useCaseId,
          ai_tool_id: generationData?.aiToolId,
          prompt_fields: generationData?.promptFields,
          generated_prompt: generationData?.generatedPrompt,
          user_ip: generationData?.userIp || null,
          user_agent: generationData?.userAgent || null
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        useCaseId: data?.use_case_id,
        aiToolId: data?.ai_tool_id,
        promptFields: data?.prompt_fields,
        generatedPrompt: data?.generated_prompt,
        userIp: data?.user_ip,
        userAgent: data?.user_agent,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error tracking generation:', error);
      throw error;
    }
  },

  /**
   * Get analytics statistics
   * @returns {Promise<Object>} Analytics statistics
   */
  async getAnalytics() {
    try {
      // Get total count
      const { count: totalCount, error: countError } = await supabase?.from('prompt_generations')?.select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      // Get this week count
      const weekAgo = new Date();
      weekAgo?.setDate(weekAgo?.getDate() - 7);
      const { count: weekCount, error: weekError } = await supabase?.from('prompt_generations')?.select('*', { count: 'exact', head: true })?.gte('created_at', weekAgo?.toISOString());

      if (weekError) throw weekError;

      // Get today count
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const { count: todayCount, error: todayError } = await supabase?.from('prompt_generations')?.select('*', { count: 'exact', head: true })?.gte('created_at', today?.toISOString());

      if (todayError) throw todayError;

      // Get most popular AI tool
      const { data: toolData, error: toolError } = await supabase?.from('prompt_generations')?.select('ai_tool_id, ai_tools(name)');

      if (toolError) throw toolError;

      const toolCounts = {};
      toolData?.forEach(item => {
        const toolName = item?.ai_tools?.name;
        if (toolName) {
          toolCounts[toolName] = (toolCounts?.[toolName] || 0) + 1;
        }
      });

      const mostPopularTool = Object.keys(toolCounts)?.length > 0
        ? Object.keys(toolCounts)?.reduce((a, b) => toolCounts?.[a] > toolCounts?.[b] ? a : b)
        : 'N/A';

      return {
        totalPrompts: totalCount || 0,
        thisWeek: weekCount || 0,
        today: todayCount || 0,
        mostPopularTool
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },

  /**
   * Get usage trend data for charts
   * @param {number} days - Number of days to fetch
   * @returns {Promise<Array>} Trend data
   */
  async getUsageTrend(days = 7) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('prompt_generations')?.select('created_at')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const dateGroups = {};
      data?.forEach(item => {
        const date = new Date(item?.created_at)?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
        dateGroups[date] = (dateGroups?.[date] || 0) + 1;
      });

      return Object.keys(dateGroups)?.map(date => ({
        date,
        prompts: dateGroups?.[date]
      }));
    } catch (error) {
      console.error('Error fetching usage trend:', error);
      throw error;
    }
  },

  /**
   * Get AI tool usage distribution
   * @returns {Promise<Array>} Tool usage data
   */
  async getToolUsage() {
    try {
      const { data, error } = await supabase?.from('prompt_generations')?.select('ai_tool_id, ai_tools(name)');

      if (error) throw error;

      const toolCounts = {};
      data?.forEach(item => {
        const toolName = item?.ai_tools?.name;
        if (toolName) {
          toolCounts[toolName] = (toolCounts?.[toolName] || 0) + 1;
        }
      });

      return Object.keys(toolCounts)?.map(tool => ({
          tool,
          count: toolCounts?.[tool]
        }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10);
    } catch (error) {
      console.error('Error fetching tool usage:', error);
      throw error;
    }
  },

  /**
   * Get use case distribution
   * @returns {Promise<Array>} Use case data
   */
  async getUseCaseDistribution() {
    try {
      const { data, error } = await supabase?.from('prompt_generations')?.select('use_case_id, use_cases(name)');

      if (error) throw error;

      const useCaseCounts = {};
      data?.forEach(item => {
        const useCaseName = item?.use_cases?.name;
        if (useCaseName) {
          useCaseCounts[useCaseName] = (useCaseCounts?.[useCaseName] || 0) + 1;
        }
      });

      return Object.keys(useCaseCounts)?.map(name => ({
        name,
        value: useCaseCounts?.[name]
      }));
    } catch (error) {
      console.error('Error fetching use case distribution:', error);
      throw error;
    }
  },

  /**
   * Get device demographics from user agent
   * @returns {Promise<Array>} Device data
   */
  async getDeviceDemographics() {
    try {
      const { data, error } = await supabase?.from('prompt_generations')?.select('user_agent');

      if (error) throw error;

      const deviceCounts = {
        'Desktop': 0,
        'Mobile': 0,
        'Tablet': 0,
        'Unknown': 0
      };

      data?.forEach(item => {
        const ua = item?.user_agent?.toLowerCase() || '';
        if (ua?.includes('iphone') || ua?.includes('android') && !ua?.includes('tablet')) {
          deviceCounts['Mobile']++;
        } else if (ua?.includes('ipad') || ua?.includes('tablet')) {
          deviceCounts['Tablet']++;
        } else if (ua?.includes('windows') || ua?.includes('macintosh') || ua?.includes('linux')) {
          deviceCounts['Desktop']++;
        } else {
          deviceCounts['Unknown']++;
        }
      });

      return Object.keys(deviceCounts)?.filter(device => deviceCounts?.[device] > 0)?.map(device => ({
          device,
          count: deviceCounts?.[device]
        }));
    } catch (error) {
      console.error('Error fetching device demographics:', error);
      throw error;
    }
  }
};